import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import password components (lazy-loaded for performance)
const PasswordsList = React.lazy(() => import("./PasswordsList"));
const NewPassword = React.lazy(() => import("./NewPassword"));
const PasswordDetails = React.lazy(() => import("./PasswordDetails"));
const PasswordPermissions = React.lazy(() => import("./PasswordPermissions"));
const PublicPasswordAccess = React.lazy(() => import("./PublicPasswordAccess"));
const MyPasswords = React.lazy(() => import("./MyPasswords"));

const passwordsModuleConfig: ModuleConfig = {
    // 🏷️ MODULE IDENTITY
    id: "passwords",
    name: "Password Management",
    description: "Secure credential management and sharing system",
    version: "1.0.0",
    enabled: true,
    category: "security",
    order: 15,
    icon: "🔐",
    color: "#9333EA",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/passwords",
            component: MyPasswords,
            permissionLevel: "view",
        },
        {
            path: "/passwords/admin",
            component: PasswordsList,
            permissionLevel: "edit",
        },
        {
            path: "/passwords/new",
            component: NewPassword,
            permissionLevel: "create",
        },
        {
            path: "/passwords/:id",
            component: PasswordDetails,
            permissionLevel: "view",
        },
        {
            path: "/passwords/permissions",
            component: PasswordPermissions,
            permissionLevel: "edit",
        },
        {
            path: "/passwords/access/:id",
            component: PublicPasswordAccess,
            permissions: [], // Public route - no permissions required
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "My Passwords",
            path: "/passwords",
            icon: "🔑",
            permissionLevel: "view",
        },
        {
            label: "All Passwords",
            path: "/passwords/admin",
            icon: "🔐",
            permissionLevel: "edit",
        },
        {
            label: "Issue Password",
            path: "/passwords/new",
            icon: "➕",
            permissionLevel: "create",
        },
        {
            label: "Permissions",
            path: "/passwords/permissions",
            icon: "⚙️",
            permissionLevel: "edit",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/passwords/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "Issue New Password",
            path: "/passwords/new",
            icon: "➕",
            description: "Issue a new password to an employee or client"
        },
        {
            label: "View My Passwords",
            path: "/passwords",
            icon: "🔑",
            description: "Access passwords issued to you"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready password management system",
                "✅ Secure credential storage and sharing",
                "✅ SMS-based public access authentication",
                "✅ 2FA secret storage and backup codes",
                "✅ Access logging and audit trails",
                "✅ Expiration management and notifications"
            ],
            notes: "Official stable release with enterprise-grade security features."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-10-15",
            title: "Enhanced Security & Permissions",
            features: [
                "✨ Enhanced PasswordPermissions component (245 lines)",
                "✨ Improved role-based access control",
                "✨ Added share password functionality",
                "✨ Better audit logging for admin actions",
                "🔒 Enhanced encryption for sensitive data"
            ],
            notes: "Security hardening and advanced permission management."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-08-08",
            title: "Brand Configuration & UI Consistency",
            features: [
                "✨ Enhanced brand configuration integration (18 lines)",
                "✨ UI consistency improvements across all password views",
                "✨ Better MyPasswords display (142 lines)",
                "✨ Improved PasswordDetails layout (187 lines)",
                "✨ Consistent color scheme and styling"
            ],
            notes: "Brand consistency and UI improvements across all password components."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-03-20",
            title: "Public Access Enhancements",
            features: [
                "✨ Enhanced PublicPasswordAccess workflow (267 insertions)",
                "✨ Improved SMS verification flow",
                "✨ Better user details capture modal integration",
                "✨ Enhanced error handling and user feedback",
                "✨ Improved mobile responsiveness"
            ],
            notes: "Public access improvements for better user experience."
        },
        {
            version: "0.3.0",
            releaseDate: "2023-12-10",
            title: "SMS Verification & Authentication",
            features: [
                "✅ Created PublicPasswordAccess component (507 lines)",
                "✨ SMS verification integration",
                "✨ LoginWithSmsModal integration",
                "✨ CaptureUserDetailsModal integration",
                "✨ Access logging for public links"
            ],
            notes: "SMS-based authentication for secure public password access."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-10-05",
            title: "Access Logging & 2FA Support",
            features: [
                "✅ Created PasswordPermissions component (187 lines)",
                "✨ Enhanced PasswordDetails with 2FA display (221 insertions)",
                "✨ Access logging for audit trails",
                "✨ 2FA secret and backup code storage",
                "✨ Expiration date tracking",
                "✨ Copy to clipboard functionality"
            ],
            notes: "Enhanced security with access logging and 2FA support."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-08-15",
            title: "Initial Password Management System",
            features: [
                "✅ Created PasswordsList component (622 lines)",
                "✅ Created NewPassword component (619 lines)",
                "✅ Created PasswordDetails component (558 lines)",
                "✅ Created MyPasswords component (422 lines)",
                "✅ Module configuration and routing (56 lines)",
                "✅ Password creation and editing",
                "✅ User assignment and sharing",
                "✅ Secure credential storage",
                "✅ Role-based permissions (PASSWORD_ADMIN, PASSWORD_USER)"
            ],
            notes: "Initial implementation with 2,277 lines of password management code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default passwordsModuleConfig;